'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { attendanceAPI, employeesAPI } from '../services/api';
import '../styles/Attendance.css';

function Attendance() {
  const { user } = useAuth();
  const { showToast } = useToast();

  const isEmployee = user?.role === 'employee';

  // Employee State
  const [myLogs, setMyLogs] = useState([]);
  const [todayMarked, setTodayMarked] = useState(false);
  const [todayRecord, setTodayRecord] = useState(null);
  const [selfNotes, setSelfNotes] = useState('');
  const [markingLoading, setMarkingLoading] = useState(false);

  // Admin State
  const [employeesList, setEmployeesList] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);

  // Modal / Form state for Admin manual entry
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [manualEmpId, setManualEmpId] = useState('');
  const [manualDate, setManualDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [manualStatus, setManualStatus] = useState('present');
  const [manualNotes, setManualNotes] = useState('');
  const [savingManual, setSavingManual] = useState(false);

  // Fetch Employee Attendance (Self View)
  const fetchMyAttendance = useCallback(async () => {
    try {
      setLoading(true);
      const res = await attendanceAPI.getMyAttendance();
      const logs = res.data.logs || [];
      setMyLogs(logs);

      // Check if marked today
      const todayStr = new Date().toISOString().split('T')[0];
      const today = logs.find(log => log.dateString === todayStr);

      if (today) {
        setTodayMarked(true);
        setTodayRecord(today);
      } else {
        setTodayMarked(false);
        setTodayRecord(null);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load attendance records', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Fetch Admin View Data
  const fetchAdminAttendance = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedDate) params.date = selectedDate;
      if (filterEmployee) params.employeeId = filterEmployee;
      if (filterStatus) params.status = filterStatus;

      const [logsRes, summaryRes, empRes] = await Promise.all([
        attendanceAPI.getAll(params),
        attendanceAPI.getSummary(selectedDate),
        employeesAPI.getAll()
      ]);

      setAdminLogs(logsRes.data.logs || []);
      setSummary(summaryRes.data.summary || null);
      setEmployeesList(empRes.data.employees || []);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load attendance data', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, filterEmployee, filterStatus, showToast]);

  useEffect(() => {
    if (isEmployee) {
      fetchMyAttendance();
    } else {
      fetchAdminAttendance();
    }
  }, [isEmployee, fetchMyAttendance, fetchAdminAttendance]);

  // Handle Employee Self Check-in
  const handleMarkSelfAttendance = async (e) => {
    e.preventDefault();
    setMarkingLoading(true);

    try {
      await attendanceAPI.markToday({ notes: selfNotes });

      showToast('Attendance marked successfully!', 'success');
      setSelfNotes('');
      fetchMyAttendance();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to mark attendance', 'error');
    } finally {
      setMarkingLoading(false);
    }
  };

  // Handle Admin Manual Entry / Update
  const handleSaveManualAttendance = async (e) => {
    e.preventDefault();
    if (!manualEmpId) {
      showToast('Please select an employee', 'error');
      return;
    }

    setSavingManual(true);
    try {
      await attendanceAPI.createOrUpdate({
        employeeId: manualEmpId,
        date: manualDate,
        status: manualStatus,
        notes: manualNotes
      });

      showToast('Attendance recorded successfully!', 'success');
      setShowAdminModal(false);
      setManualNotes('');
      fetchAdminAttendance();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to record attendance', 'error');
    } finally {
      setSavingManual(false);
    }
  };

  // Delete Record (Admin)
  const handleDeleteAttendance = async (id) => {
    if (!window.confirm('Are you sure you want to delete this attendance record?')) return;

    try {
      await attendanceAPI.delete(id);
      showToast('Attendance record deleted', 'success');
      fetchAdminAttendance();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete record', 'error');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'present':
        return <span className="status-badge status-present">Present</span>;
      case 'half-day':
        return <span className="status-badge status-halfday">Half-Day</span>;
      case 'absent':
        return <span className="status-badge status-absent">Absent</span>;
      case 'leave':
        return <span className="status-badge status-leave">Leave</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  const formattedTodayDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="attendance-page-container">
      {/* Header Banner */}
      <div className="attendance-header">
        <div>
          <h1>Employee Attendance Portal</h1>
          <p className="attendance-subtitle">
            {isEmployee
              ? `Welcome ${user?.name || 'Employee'} — Record and view your daily work attendance`
              : 'Admin Attendance Control & Live Monitoring'}
          </p>
        </div>
        <div className="attendance-date-chip">
          📅 {formattedTodayDate}
        </div>
      </div>

      {/* RENDER EMPLOYEE VIEW */}
      {isEmployee ? (
        <div className="employee-attendance-view">
          {/* Action Card */}
          <div className="today-mark-card">
            <h2>Today's Attendance Status</h2>

            {todayMarked ? (
              <div className="already-marked-box">
                <div className="marked-icon">✅</div>
                <div>
                  <h3>Attendance Marked for Today</h3>
                  <p>
                    Status: {getStatusBadge(todayRecord?.status)} | Marked at:{' '}
                    {new Date(todayRecord?.checkInTime || todayRecord?.createdAt).toLocaleTimeString()}
                  </p>
                  {todayRecord?.notes && <p className="notes-text">Notes: "{todayRecord.notes}"</p>}
                </div>
              </div>
            ) : (
              <form onSubmit={handleMarkSelfAttendance} className="mark-form">
                <p>Click below to mark your attendance for today ({new Date().toLocaleDateString()}):</p>
                
                <div className="form-row">
                  <div className="form-group flex-2">
                    <label>Work Notes / Shift (Optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. Morning shift, Wedding catering service..."
                      value={selfNotes}
                      onChange={(e) => setSelfNotes(e.target.value)}
                      className="attendance-input"
                    />
                  </div>
                </div>

                <button type="submit" disabled={markingLoading} className="mark-submit-btn">
                  {markingLoading ? 'Marking...' : 'Mark Attendance Now'}
                </button>
              </form>
            )}
          </div>

          {/* History Log */}
          <div className="attendance-history-card">
            <h2>My Attendance History</h2>
            {loading ? (
              <div className="loading-spinner">Loading attendance records...</div>
            ) : myLogs.length === 0 ? (
              <p className="no-records">No attendance records found yet.</p>
            ) : (
              <div className="table-responsive">
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Check-in Time</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myLogs.map((log) => (
                      <tr key={log._id}>
                        <td>{log.dateString || new Date(log.date).toLocaleDateString()}</td>
                        <td>{getStatusBadge(log.status)}</td>
                        <td>{new Date(log.checkInTime || log.createdAt).toLocaleTimeString()}</td>
                        <td>{log.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* RENDER ADMIN / OWNER VIEW */
        <div className="admin-attendance-view">
          {/* Summary KPI Cards */}
          {summary && (
            <div className="kpi-grid">
              <div className="kpi-card blue">
                <div className="kpi-title">Total Active Employees</div>
                <div className="kpi-value">{summary.totalEmployees}</div>
              </div>
              <div className="kpi-card green">
                <div className="kpi-title">Present Today</div>
                <div className="kpi-value">{summary.present}</div>
              </div>
              <div className="kpi-card orange">
                <div className="kpi-title">Pending Mark</div>
                <div className="kpi-value">{summary.pending}</div>
              </div>
              <div className="kpi-card red">
                <div className="kpi-title">Absent / Leave</div>
                <div className="kpi-value">{summary.absent + summary.leave + summary.halfDay}</div>
              </div>
            </div>
          )}

          {/* Filter & Action Toolbar */}
          <div className="admin-toolbar">
            <div className="filters-group">
              <div className="filter-item">
                <label>Date Filter:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="toolbar-input"
                />
              </div>

              <div className="filter-item">
                <label>Employee:</label>
                <select
                  value={filterEmployee}
                  onChange={(e) => setFilterEmployee(e.target.value)}
                  className="toolbar-select"
                >
                  <option value="">All Employees</option>
                  {employeesList.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name} ({emp.category})
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-item">
                <label>Status:</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="toolbar-select"
                >
                  <option value="">All Statuses</option>
                  <option value="present">Present</option>
                  <option value="half-day">Half-Day</option>
                  <option value="absent">Absent</option>
                  <option value="leave">Leave</option>
                </select>
              </div>
            </div>

            <button
              onClick={() => setShowAdminModal(true)}
              className="add-record-btn"
            >
              + Mark / Edit Employee Attendance
            </button>
          </div>

          {/* Admin Attendance Records Table */}
          <div className="attendance-history-card">
            <h2>Employee Attendance Records ({adminLogs.length})</h2>
            {loading ? (
              <div className="loading-spinner">Loading attendance records...</div>
            ) : adminLogs.length === 0 ? (
              <p className="no-records">No attendance records found for the selected criteria.</p>
            ) : (
              <div className="table-responsive">
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Employee Name</th>
                      <th>Category</th>
                      <th>Date</th>
                      <th>Check-in Time</th>
                      <th>Status</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminLogs.map((log) => (
                      <tr key={log._id}>
                        <td className="emp-name-cell">
                          {log.employee?.name || 'Unknown Employee'}
                        </td>
                        <td>
                          <span className="cat-badge">{log.employee?.category || 'staff'}</span>
                        </td>
                        <td>{log.dateString || new Date(log.date).toLocaleDateString()}</td>
                        <td>{new Date(log.checkInTime || log.createdAt).toLocaleTimeString()}</td>
                        <td>{getStatusBadge(log.status)}</td>
                        <td>{log.notes || '-'}</td>
                        <td>
                          <button
                            onClick={() => handleDeleteAttendance(log._id)}
                            className="delete-icon-btn"
                            title="Delete Record"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Admin Modal for Recording Attendance */}
          {showAdminModal && (
            <div className="modal-backdrop">
              <div className="modal-card">
                <div className="modal-header">
                  <h3>Record / Update Employee Attendance</h3>
                  <button onClick={() => setShowAdminModal(false)} className="close-btn">&times;</button>
                </div>

                <form onSubmit={handleSaveManualAttendance} className="modal-form">
                  <div className="form-group">
                    <label>Select Employee *</label>
                    <select
                      value={manualEmpId}
                      onChange={(e) => setManualEmpId(e.target.value)}
                      className="attendance-select"
                      required
                    >
                      <option value="">-- Choose Employee --</option>
                      {employeesList.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.name} ({emp.category} - ₹{emp.dailySalary}/day)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Date *</label>
                    <input
                      type="date"
                      value={manualDate}
                      onChange={(e) => setManualDate(e.target.value)}
                      className="attendance-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Status *</label>
                    <select
                      value={manualStatus}
                      onChange={(e) => setManualStatus(e.target.value)}
                      className="attendance-select"
                      required
                    >
                      <option value="present">Present</option>
                      <option value="half-day">Half-Day</option>
                      <option value="absent">Absent</option>
                      <option value="leave">Leave</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Notes / Comments</label>
                    <input
                      type="text"
                      placeholder="e.g. Approved leave, Event shift..."
                      value={manualNotes}
                      onChange={(e) => setManualNotes(e.target.value)}
                      className="attendance-input"
                    />
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      onClick={() => setShowAdminModal(false)}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={savingManual}
                      className="save-btn"
                    >
                      {savingManual ? 'Saving...' : 'Save Attendance'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Attendance;
